import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { addRolesPermission, getRolesItem } from "../../../store/roles/slice";
import { useAppSelector, useAppDispatch } from "../../../store/index";
import { usePermissions } from "../../../helpers/usePermission";
import {
  NotificationTypes,
  showNotification,
} from "../../../store/notifications/slice";

import arrowIcon from "../../../assets/icons/sidebar/arrow-down.svg";

const permissionActions = [
  "create",
  "read",
  "update",
  "delete",
  "export",
  "sensitive_data",
] as const;

type PermissionAction = (typeof permissionActions)[number];

// These actions are not implemented by the corresponding Back Office pages.
const unsupportedPermissions: Record<string, PermissionAction[]> = {
  risk_limits: ["create", "delete"],
  risk_permissions: ["create", "delete"],
  payments_currencies: ["create", "delete"],
  game_center_parsing: ["create", "delete"],
  game_center_provider: ["delete"],
  game_center_games_new: ["create", "delete"],
  game_center_games_active: ["create", "delete"],
  game_center_games_hidden: ["create", "delete"],
  game_center_games_deactivated: ["create", "delete"],
  game_center_games_no_category: ["create", "delete"],
  live_ops_bonuses_list: ["delete"],
  live_ops_bonuses_free_spins: ["delete"],
  live_ops_wager_games_not_processing: ["delete"],
  live_ops_cashback_games_not_processing: ["delete"],
  players_list: ["delete"],
  risk_players: ["delete"],
  risk_kyc_documents: ["delete"],
  risk_kyc_rules: ["delete"],
  payments_transactions_operations: ["delete"],
  analytic_global_common: ["create", "update", "delete"],
  analytic_global_users: ["create", "update", "delete"],
  analytic_sports_categories: ["create", "update", "delete"],
  analytic_sports_competitions: ["create", "update", "delete"],
  analytic_payments_methods: ["create", "update", "delete"],
  analytic_payments_providers: ["create", "update", "delete"],
  analytic_payments_deposits: ["create", "update", "delete"],
  analytic_casino_games: ["create", "update", "delete"],
  analytic_casino_providers: ["create", "update", "delete"],
};

const normalizePermissionKey = (key: string) =>
  key.toLowerCase().replace(/\s+/g, "_");

const Sidebar: React.FC = () => {
  const [activeModule, setActiveModule] = useState<string>("");
  const [activeSubsegment, setActiveSubsegment] = useState<string>("");
  const [modules, setModules] = useState<string[]>([]);
  const [subModules, setSubModules] = useState<{ [key: string]: string[] }>();
  const [permissionFetching, setPermissionFetching] = useState<boolean>(false);
  const [loadingRows, setLoadingRows] = useState<Record<string, boolean>>({});
  const lastPermissionWarningAt = useRef(0);

  const activePermissions = usePermissions("roles_list");
  const isPermissionUpdateDenied =
    activePermissions !== null && !activePermissions?.includes("update");

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const allModules = useAppSelector((store) => store.settings.modules);
  const activeItem = useAppSelector((store) => store.roles.item);
  const isSelectedSystemRole =
    activeItem?.isSystem === true ||
    ["player", "admin"].includes(activeItem?.title?.toLowerCase() ?? "");

  useEffect(() => {
    if (allModules) {
      setModules(Object.keys(allModules as object).map((el) => el));
    }
  }, [allModules]);

  useEffect(() => {
    setActiveModule(modules[0]);
    // @ts-ignore
    if (allModules) setSubModules(allModules[modules[0]]);
  }, [modules, allModules]);

  const getPermissionKey = (segment: string, subSegment?: string) =>
    `${activeModule?.toLowerCase()}_${segment?.toLowerCase()}${subSegment ? `_${subSegment.toLowerCase()}` : ""}`;

  const getRowKey = (segment: string, subSegment?: string) =>
    `${activeModule}:${segment}:${subSegment ?? "main"}`;

  const isPermissionSupported = (
    segment: string,
    action: PermissionAction,
    subSegment?: string,
  ): boolean => {
    if (!subSegment) {
      const children = subModules?.[segment] ?? [];
      if (children.length) {
        return children.some((child) =>
          isPermissionSupported(segment, action, child),
        );
      }
    }

    return !unsupportedPermissions[
      normalizePermissionKey(getPermissionKey(segment, subSegment))
    ]?.includes(action);
  };

  const getIsCheckedClass = (
    segment: string,
    action: PermissionAction,
    subSegment?: string,
  ) => {
    const children = !subSegment ? (subModules?.[segment] ?? []) : [];
    if (children.length) {
      const supportedChildren = children.filter((child) =>
        isPermissionSupported(segment, action, child),
      );
      const checkedCount = supportedChildren.filter((child) =>
        activeItem?.permissions?.[getPermissionKey(segment, child)]?.includes(
          action,
        ),
      ).length;

      if (checkedCount === 0) return undefined;
      return checkedCount === supportedChildren.length
        ? "checked"
        : "indeterminate";
    }

    return activeItem?.permissions?.[getPermissionKey(segment, subSegment)]?.includes(
      action,
    )
      ? "checked"
      : undefined;
  };

  const isCheckboxDisabled = (
    segment: string,
    action: PermissionAction,
    subSegment?: string,
  ) =>
    !isPermissionSupported(segment, action, subSegment) ||
    permissionFetching ||
    isPermissionUpdateDenied;

  const renderPermissionControl = (
    segment: string,
    action: PermissionAction,
    subSegment?: string,
  ) => {
    const isUnsupported = !isPermissionSupported(segment, action, subSegment);
    const checkedClass = isUnsupported
      ? undefined
      : getIsCheckedClass(segment, action, subSegment);
    const disabled = isCheckboxDisabled(segment, action, subSegment);

    return (
      <div className="role-input" key={action}>
        <div
          onClick={() => {
            if (disabled) return;
            handleTogglePermission(
              segment,
              action,
              checkedClass === "checked" ? 0 : 1,
              subSegment,
            );
          }}
          className={`role-input--control ${checkedClass ?? ""} ${disabled ? "disabled" : ""} ${isUnsupported ? "unavailable" : ""}`}
          title={isUnsupported ? "This action is not available in this section" : undefined}
          aria-disabled={disabled}
          aria-checked={checkedClass === "indeterminate" ? "mixed" : checkedClass === "checked"}
          role="checkbox"
        />
      </div>
    );
  };

  const handleTogglePermission = async (
    segment: string,
    action: PermissionAction,
    value: number,
    subSegment?: string,
  ) => {
    const rowKey = getRowKey(segment, subSegment);
    setPermissionFetching(true);
    setLoadingRows((rows) => ({ ...rows, [rowKey]: true }));

    const children = !subSegment ? (subModules?.[segment] ?? []) : [];
    const targetSubsegments = children.length
      ? children.filter((child) => isPermissionSupported(segment, action, child))
      : [subSegment];

    try {
      for (const targetSubsegment of targetSubsegments) {
        await dispatch(
          addRolesPermission({
            actionName: action,
            actionValue: value,
            section1: activeModule,
            section2: segment,
            section3: targetSubsegment,
            role: activeItem?.title ?? "",
          }),
        ).unwrap();
      }
    } catch {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("unknownError"),
          type: NotificationTypes.error,
        }),
      );
    } finally {
      await dispatch(getRolesItem(activeItem?.id ?? 0));
      setLoadingRows((rows) => {
        const nextRows = { ...rows };
        delete nextRows[rowKey];
        return nextRows;
      });
      setPermissionFetching(false);
    }
  };

  const renderInputs = (segment: string) => {
    return (
      <div className="segment-inputs">
        {permissionActions.map((action) =>
          renderPermissionControl(segment, action),
        )}
      </div>
    );
  };
  const renderInputsSubsegment = (segment: string, subSegment: string) => {
    return (
      <div className="subsegment-inputs">
        {permissionActions.map((action) =>
          renderPermissionControl(segment, action, subSegment),
        )}
      </div>
    );
  };

  const handleDisabledCheckboxClick = (
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    const target = event.target as HTMLElement;
    const checkbox = target.closest(".role-input--control");
    const isDeniedCheckbox =
      checkbox?.classList.contains("disabled") && isPermissionUpdateDenied;

    if (!checkbox || (!isSelectedSystemRole && !isDeniedCheckbox)) {
      return;
    }

    event.stopPropagation();

    const now = Date.now();
    if (now - lastPermissionWarningAt.current < 3000) return;
    lastPermissionWarningAt.current = now;

    dispatch(
      showNotification({
        title: t("warning"),
        message: isSelectedSystemRole
          ? "Permissions for system roles cannot be edited."
          : "You don't have permission to edit role permissions.",
        type: NotificationTypes.warning,
      }),
    );
  };

  return (
    <div
      className={`roles-table-wrapper ${
        permissionFetching ? "roles-table-wrapper--permission-fetching" : ""
      }`}
      onClickCapture={handleDisabledCheckboxClick}
    >
      <div className="roles-table-header">
        <div className="item">{t("mainSegment")}</div>
        <div className="item">{t("subSegment")}</div>
        <div className="item">{t("create")}</div>
        <div className="item">{t("read")}</div>
        <div className="item">{t("update")}</div>
        <div className="item">{t("delete")}</div>
        <div className="item">{t("export")}</div>
        <div className="item">{t("readUserContacts")}</div>
      </div>
      <div className="roles-table-body">
        <div className="item permissions">
          {modules.map((el) => (
            <div
              onClick={() => {
                setActiveModule(el);
                // @ts-ignore
                setSubModules(allModules[el]);
              }}
              className={`permission ${activeModule === el ? "active" : ""}`}
              key={el}
            >
              {el}
            </div>
          ))}
        </div>
        <div className="item segments">
          {subModules &&
            Object.keys(subModules).map((el) => (
              <div className="segment" key={el}>
                <div
                  className={`segment-main-row ${loadingRows[getRowKey(el)] ? "permission-row-loading" : ""}`}
                  aria-busy={Boolean(loadingRows[getRowKey(el)])}
                >
                  <div
                    className={`row ${activeSubsegment === el ? "opened" : ""}`}
                    onClick={() => {
                      if (activeSubsegment === el) {
                        setActiveSubsegment("");
                      } else {
                        setActiveSubsegment(el);
                      }
                    }}
                  >
                    <span>{el}</span>
                    {subModules[el].length > 0 ? (
                      <img src={arrowIcon} alt="" />
                    ) : null}
                  </div>
                  {renderInputs(el)}
                  {loadingRows[getRowKey(el)] ? (
                    <div className="role-row-loader" aria-label={t("loading")}>
                      <span />
                      <span />
                      <span />
                    </div>
                  ) : null}
                </div>
                {subModules[el].length > 0 ? (
                  <div
                    className={`subsegments ${activeSubsegment === el ? "opened" : ""}`}
                  >
                    {subModules[el].map((item) => (
                      <div
                        className={`subsegment ${loadingRows[getRowKey(el, item)] ? "permission-row-loading" : ""}`}
                        key={item}
                        aria-busy={Boolean(loadingRows[getRowKey(el, item)])}
                      >
                        <div className="subsegment-row">{item}</div>
                        {renderInputsSubsegment(el, item)}
                        {loadingRows[getRowKey(el, item)] ? (
                          <div className="role-row-loader" aria-label={t("loading")}>
                            <span />
                            <span />
                            <span />
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
